import { useGetFeatureFlags } from '@/hooks/useGetFeatureFlags';
import DynamicMap from './DynamicMap';
import StandardMap from './StandardMap';

/**
 * Base map component for rendering a map including the legend and settings panel
 * @param {object} props
 * @param {string} props.mapContainerId - the id of the map container
 * @param {object} props.map - the part of state set for the map
 * @param {function} props.setMap - function to set the map in state
 * @param {string} props.style (optional) - the style for map (default satellite if no user option)
 * @param {function} props.setStyle (optional) - function to set the style in state
 * @param {array} props.layers (optional) - array of layers to render (default none)
 * @param {array} props.layerFilters (optional) - array of filters to apply to the map layers (default none)
 *  ex: [
 *    {
 *      entityTypes: ['assets', 'blocks'],
 *      filter: ['match', ['get', 'countryIsoCode'], ['USA'], true, false]
 *    }
 *  ]
 * @param {object} props.layerStyle (optional) - object to style a layer based on metric and sizeType (default none)
 *  ex: {
 *    layer: 'co2 emitters',
 *    metric: 'Total CO2 Emissions',
 *    sizeType: 'Total Abatement Cost',
 *  }
 * @param {array} props.hideLayers (optional) - array of layers to hide at time of map creation (default none)
 * @param {boolean} props.enableEntityHighlight (optional) - flag for including entity type highlight layers (default off)
 * @param {boolean} props.enableInfoPopup (optional) - flag for including the info popup (default off)
 * @param {function} props.layerClickCallback (optional) - function for when a user clicks an item on the map
 *  (default nothing happens)
 * @param {object} props.legendProps (optional) - props for legend component
 *  ex (non-tabbed): {
      enabled: true, // flag for including the legend (default off)
      items: [ // array of items to display for the map legend (default none)
        { color: WELL_COLORS.exploration_and_appraisal, title: 'Exploration' },
        { color: WELL_COLORS.production, title: 'Production' },
        { color: WELL_COLORS.injection, title: 'Injection' },
        { color: WELL_COLORS.wells_to_watch, title: 'E&A Wells to Watch' },
        { color: WELL_COLORS.default, title: 'Other' },
      ],
      title: 'Legend: Wells', // title for the legend (default none)
    }
    ex (tabbed): {
      enabled: open && showLegend, // flag for including the legend (default off)
      keepMounted: true, // flag for keeping the legend mounted when closed - this enables transition effect (default off)
      tabbed: true, // flag for using tabbed legend (default off)
      tabbedItems: { // array of tabbed items to display for the map legend (default none)
        label: 'CCUS Project Industry Types', // tab label
        multiline: true, // flag for allowing the legend to take up multiple lines (default false)
        items: INDUSTRY_LEGEND_ITEMS, // array of items to display for the map legend - follows same format as non-tabbed
          items above
        itemBorder: 'solid thin white', // border color for the legend items (default black)
      },
    }
 * @param {boolean} props.control (optional) - flag for including the map controls (default off)
 * @param {boolean} props.ruler (optional) - flag for including ruler feature (default off)
 * @param {boolean} props.rulerActive (optional) - boolean if the ruler is activated (default false)
 * @param {function} props.setRulerActive (optional) - function to toggle the ruler feature
 * @param {object} props.styles (optional) - styles to override the default classes
 * @param {object} props.settingsProps (optional) - props for settings component
 *  ex: {
 *    enabled: true,
 *    includeMapStyle: true,
 *    includeLayers: true,
 *    layerProps: {
 *      defaultLayer: 'productionHeatMap', // the default layer to render (optional, default none)
 *      items: [], // items can be found in ./settings/constants.js (optional, default ALL)
 *      filters: {}, // filters to pass to layer query (optional, default none)
 *    },
 *  }
 * @param {object} props.syncMap (optional) - reference to another part of state set for a map to sync actions with
 *  (default none)
 * @param {object} props.callbacks (optional) - callbacks to attach to mapbox events
 *  ex: {
 *    zoomend: () => {},
 *  }
 * @param {object} props.countryClickProps (optional) - params for interactive countries (optional, default none)
 *  ex: {
 *    enabled: true,
 *    filters: {}, // filters to pass to query for available user countries
 *    selectedCountry: {} // country object that is the user selected country
 *  }
 * @param {function} props.removeLayerCallback (optional) - callback for when a layer is disabled
 * @param {object} props.defaultMapPosition (optional) - the position of the map (default from url if exists)
 *  ex: {
 *    latLong: `[${map.getCenter().lng}, ${map.getCenter().lat}]`, -> "[0.00000, 0.00000]"
 *    zoom: map.getZoom(), -> 0.00
 *  }
 * @param {string} props.tilesetType (optional) - the type of tilesets to use (default MAP_TILESET_TYPES.oilAndGasMap)
 */
export const MapInstance = props => {
  const { mapboxPoc } = useGetFeatureFlags();

  return mapboxPoc ? <DynamicMap {...props} /> : <StandardMap {...props} />;
};
